import type { ReactNode, SVGProps } from 'react';

/**
 * A small stroke icon set on a 24px grid. Inline rather than a font or sprite
 * so the app has no external requests and works offline from first paint.
 */
const paths: Record<string, ReactNode> = {
  // rooms
  bed: (
    <>
      <path d="M3 18v-7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7" />
      <path d="M3 14h18M3 18v2M21 18v2" />
      <path d="M7 9V7.5A1.5 1.5 0 0 1 8.5 6h7A1.5 1.5 0 0 1 17 7.5V9" />
    </>
  ),
  bath: (
    <>
      <path d="M3 11h18v3a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z" />
      <path d="M6 11V6a2 2 0 0 1 4 0" />
      <path d="M6 18v2M18 18v2" />
    </>
  ),
  sofa: (
    <>
      <path d="M4 12V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4" />
      <path d="M3 12a2 2 0 0 1 2 2v3h14v-3a2 2 0 1 1 4 0v5H1v-5a2 2 0 0 1 2-2z" />
    </>
  ),
  kitchen: (
    <>
      <path d="M5 3h6v18H5zM8 7v4" />
      <path d="M15 3c2 0 3 1.5 3 4v5h-3z" />
      <path d="M16.5 12v9" />
    </>
  ),
  desk: (
    <>
      <path d="M3 10h18M4 10V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3" />
      <path d="M5 10v11M19 10v11M9 14h6" />
    </>
  ),
  stairs: <path d="M3 21v-4h4v-4h4V9h4V5h6" />,
  door: (
    <>
      <path d="M6 21V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17" />
      <path d="M4 21h16M14.5 12.5v1" />
    </>
  ),
  dumbbell: (
    <>
      <path d="M6.5 7v10M3.5 9v6M17.5 7v10M20.5 9v6M6.5 12h11" />
    </>
  ),
  shirt: <path d="M9 3 4 6l1.5 4L8 9v12h8V9l2.5 1L20 6l-5-3-3 2z" />,
  toilet: (
    <>
      <path d="M6 4h5v6h5a4 4 0 0 1-4 4H9a3 3 0 0 1-3-3z" />
      <path d="M9 14l-1 7h7l-1-4" />
    </>
  ),
  tree: (
    <>
      <path d="M12 3 6 12h3l-3 5h12l-3-5h3z" />
      <path d="M12 17v4" />
    </>
  ),
  'hot-tub': (
    <>
      <path d="M3 12h18v4a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z" />
      <path d="M7 9c0-1.5 1.5-1.5 1.5-3S7 3 7 3M12 9c0-1.5 1.5-1.5 1.5-3S12 3 12 3M17 9c0-1.5 1.5-1.5 1.5-3S17 3 17 3" />
    </>
  ),

  // devices
  ceiling: (
    <>
      <path d="M12 3v3M7 14a5 5 0 0 1 10 0z" />
      <path d="M5 14h14M10 18h4" />
    </>
  ),
  pendant: (
    <>
      <path d="M12 3v6M8 15a4 4 0 0 1 8 0z" />
      <path d="M7 15h10" />
    </>
  ),
  lamp: (
    <>
      <path d="M8 4h8l2 7H6z" />
      <path d="M12 11v8M8.5 19h7" />
    </>
  ),
  wall: (
    <>
      <path d="M5 4v16" />
      <path d="M9 8h6a3 3 0 0 1 3 3v1H9z" />
      <path d="M9 12h9" />
    </>
  ),
  mirror: (
    <>
      <rect x="6" y="3" width="12" height="14" rx="6" />
      <path d="M12 17v4M9 21h6" />
    </>
  ),
  niche: (
    <>
      <rect x="5" y="4" width="14" height="16" rx="2" />
      <path d="M9 4v16M15 4v16" />
    </>
  ),
  strip: (
    <>
      <rect x="2" y="10" width="20" height="4" rx="2" />
      <path d="M6 14v3M12 14v3M18 14v3" />
    </>
  ),
  cabinet: (
    <>
      <rect x="4" y="3" width="16" height="12" rx="2" />
      <path d="M12 3v12M7 18h10" />
    </>
  ),
  shelf: (
    <>
      <path d="M4 5h16M4 12h16M4 19h16" />
      <path d="M7 5v7M10 5v7M14 12v7M17 12v7" />
    </>
  ),
  tv: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>
  ),
  tablet: (
    <>
      <rect x="6" y="3" width="12" height="18" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  ac: (
    <>
      <rect x="3" y="5" width="18" height="7" rx="2" />
      <path d="M7 16c0 1.5 1.5 1.5 1.5 3M12 16c0 1.5 1.5 1.5 1.5 3M17 16c0 1.5 1.5 1.5 1.5 3" />
    </>
  ),
  radiator: (
    <>
      <rect x="4" y="5" width="16" height="12" rx="2" />
      <path d="M8 5v12M12 5v12M16 5v12M7 20h10" />
    </>
  ),
  heater: (
    <>
      <path d="M8 3c0 3-2 3-2 6s2 3 2 6M16 3c0 3-2 3-2 6s2 3 2 6" />
      <path d="M4 20h16" />
    </>
  ),
  fan: (
    <>
      <circle cx="12" cy="12" r="2" />
      <path d="M12 10c0-4 1-6 3-6s2 3-1 5M14 12c4 0 6 1 6 3s-3 2-5-1M12 14c0 4-1 6-3 6s-2-3 1-5M10 12c-4 0-6-1-6-3s3-2 5 1" />
    </>
  ),
  shower: (
    <>
      <path d="M6 21V7a3 3 0 0 1 6 0" />
      <path d="M9 11h10a2 2 0 0 1-2 2h-6a2 2 0 0 1-2-2z" />
      <path d="M12 16v1M15 17v1M18 16v1" />
    </>
  ),
  boost: <path d="M13 2 5 14h6l-1 8 8-12h-6z" />,
  tap: (
    <>
      <path d="M8 21h8M12 21v-6" />
      <path d="M6 9h6V6a3 3 0 0 1 6 0v1" />
      <path d="M6 9v2h6V9" />
    </>
  ),
  pump: (
    <>
      <circle cx="12" cy="13" r="5" />
      <path d="M12 8V4h4M17 13h4" />
    </>
  ),
  filter: <path d="M4 5h16l-6 7v7l-4-2v-5z" />,
  thermometer: (
    <>
      <path d="M12 14V4a2 2 0 0 1 4 0v10a4 4 0 1 1-4 0z" />
      <circle cx="14" cy="17" r="1.4" />
    </>
  ),
  outdoor: (
    <>
      <path d="M12 3v2M5 12H3M21 12h-2M6 6 4.5 4.5M18 6l1.5-1.5" />
      <circle cx="12" cy="13" r="4" />
      <path d="M9 20h6" />
    </>
  ),

  // status + chrome
  shield: <path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z" />,
  'shield-check': (
    <>
      <path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="2.5" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  unlock: (
    <>
      <rect x="4" y="10" width="16" height="11" rx="2.5" />
      <path d="M8 10V7a4 4 0 0 1 7.4-2" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="12" r="4" />
      <path d="M12 12h9M18 12v3M15 12v2" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1 1" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1-1" />
    </>
  ),
  music: (
    <>
      <path d="M9 18V5l11-2v13" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="17.5" cy="16" r="2.5" />
    </>
  ),
  camera: (
    <>
      <path d="M3 8a2 2 0 0 1 2-2h3l1.5-2h5L16 6h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <circle cx="12" cy="12.5" r="3.5" />
    </>
  ),
  motion: (
    <>
      <circle cx="12" cy="5" r="2" />
      <path d="M12 8v5l3 3M12 13l-3 3M9 10l3-1 3 1" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
    </>
  ),
  moon: <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />,
  sparkle: <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />,
  power: (
    <>
      <path d="M12 3v9" />
      <path d="M7 6a7.5 7.5 0 1 0 10 0" />
    </>
  ),
  chevron: <path d="m9 5 7 7-7 7" />,
  play: <path d="M8 5.5v13l11-6.5z" strokeLinejoin="round" />,
  pause: <path d="M9.5 5v14M14.5 5v14" />,
  prev: <path d="M17 5.5v13L7 12zM5 5v14" />,
  next: <path d="M7 5.5v13L17 12zM19 5v14" />,
  'chevron-down': <path d="m5 9 7 7 7-7" />,
  back: <path d="m14 5-7 7 7 7" />,
  check: <path d="m5 13 4 4L19 7" />,
  close: <path d="M6 6l12 12M18 6 6 18" />,
  plus: <path d="M12 5v14M5 12h14" />,
  share: (
    <>
      <path d="M12 15V4M8.5 7.5 12 4l3.5 3.5" />
      <path d="M5 13v6a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-6" />
    </>
  ),
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  wifi: (
    <>
      <path d="M2.5 9a15 15 0 0 1 19 0M5.5 12.5a10.5 10.5 0 0 1 13 0M8.5 16a6 6 0 0 1 7 0" />
      <circle cx="12" cy="19.5" r="1" />
    </>
  ),
  'wifi-off': (
    <>
      <path d="M3 3l18 18" />
      <path d="M8.5 16a6 6 0 0 1 7 0" />
      <circle cx="12" cy="19.5" r="1" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8v.5" />
    </>
  ),
  home: (
    <>
      <path d="M4 11 12 4l8 7" />
      <path d="M6 10v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9" />
    </>
  ),
  rooms: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="2" />
      <rect x="13" y="3" width="8" height="8" rx="2" />
      <rect x="3" y="13" width="8" height="8" rx="2" />
      <rect x="13" y="13" width="8" height="8" rx="2" />
    </>
  ),
  download: (
    <>
      <path d="M12 4v10M8.5 10.5 12 14l3.5-3.5" />
      <path d="M5 17v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2" />
    </>
  ),
  device: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2.5" />
      <path d="M11 18.5h2" />
    </>
  ),
  bulb: (
    <>
      <path d="M9 17a5.5 5.5 0 1 1 6 0v2H9z" />
      <path d="M10 22h4" />
    </>
  ),
  blind: (
    <>
      <path d="M3 4h18M4 4v13M20 4v13" />
      <path d="M6 8h12M6 12h12M6 16h12" />
      <path d="M12 17v3" />
    </>
  ),
};

export type IconName = keyof typeof paths;

export function Icon({
  name,
  size = 20,
  strokeWidth = 1.6,
  ...rest
}: { name: string; size?: number; strokeWidth?: number } & SVGProps<SVGSVGElement>): ReactNode {
  const d = paths[name] ?? paths['bulb'];
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {d}
    </svg>
  );
}
